import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from '../components/LegalPageLayout';
import { useLocale } from '../contexts/LocaleContext';

const CONTACT = 'contact@adfini.ai';

function ExternalPrivacyLink({ href, children }) {
  return (
    <a
      href={href}
      className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

export default function PrivacyPolicy() {
  const { locale } = useLocale();
  const lastUpdated = 'April 30, 2026';

  return (
    <LegalPageLayout title="Privacy Policy" locale={locale}>
      <p className="text-[var(--studio-ink)]">Last updated: {lastUpdated}</p>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Who we are</h2>
        <p>
          Adfini (“we”, “us”) provides the Adfini web application and related services. This policy
          describes how we collect, use, and share information when you use our services.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Information we collect</h2>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-[var(--studio-ink)]">Account and profile:</strong> name, email address,
            authentication identifiers, role, and credit balances when you register or sign in.
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Authentication:</strong> if you use Google sign-in,
            Google processes login according to their policies; we receive profile and account
            identifiers needed to operate your session.
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Content you upload:</strong> images and other files you
            submit for creative workflows, plus related metadata (filenames, types, sizes, storage
            locations).
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Generation and project data:</strong> workflow settings,
            job status, approvals, outputs (e.g. URLs or references to generated assets), and linkage
            to our generation backend (“IndieHouse” engine) where applicable.
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Billing:</strong> payment-related records processed
            through our payment provider (e.g. checkout session identifiers, package purchased,
            amount, currency, payment status, email on the transaction).
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Support and communications:</strong> information you send
            when you contact us (such as your email and message contents).
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Technical data:</strong> standard server and application
            logs, security signals, and data from your browser or device needed to run the service
            (e.g. IP address, user agent) as typical for hosted web applications.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">How we use information</h2>
        <p>We use the information above to:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Provide, operate, and secure the service</li>
          <li>Authenticate users and maintain sessions</li>
          <li>Process generations, store uploads and outputs, and manage credits</li>
          <li>Process payments and fulfill purchases</li>
          <li>Send transactional emails (e.g. password reset) where enabled</li>
          <li>Comply with law, enforce our terms, and prevent abuse</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Third-party services and their privacy policies</h2>
        <p>
          We use the providers below to run Adfini. They may process personal data as independent
          controllers or as processors on our behalf, depending on the service. Their practices are
          governed by their own policies—we do not control how they handle information on their
          systems. Links open in a new tab.
        </p>
        <ul className="list-disc space-y-4 pl-5">
          <li>
            <strong className="text-[var(--studio-ink)]">Google</strong> (including{' '}
            <ExternalPrivacyLink href="https://policies.google.com/privacy">
              Google Privacy Policy
            </ExternalPrivacyLink>
            ) — when you use <strong className="text-[var(--studio-ink)]">Sign in with Google</strong>, Google
            processes authentication and may collect data under Google&apos;s policy. We receive the
            profile and identifiers needed to create and maintain your account session.
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Firebase</strong> (Google){' '}
            <ExternalPrivacyLink href="https://firebase.google.com/support/privacy">
              Privacy and Security in Firebase
            </ExternalPrivacyLink>{' '}
            — we use Firebase Authentication on the client for sign-in and session tokens; Google
            describes how Firebase-related data is handled in the linked materials.
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Stripe</strong>{' '}
            <ExternalPrivacyLink href="https://stripe.com/privacy">
              Privacy Policy
            </ExternalPrivacyLink>{' '}
            — processes payments when you purchase credit packs. Stripe receives payment and
            billing-related information needed to complete the transaction; we store limited payment
            records in our systems as described above.
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Resend</strong>{' '}
            <ExternalPrivacyLink href="https://resend.com/legal/privacy-policy">
              Privacy Policy
            </ExternalPrivacyLink>{' '}
            — delivers transactional email (for example password reset) when that feature is enabled.
            Resend may process recipient addresses and message metadata as part of delivery.
          </li>
          <li>
            <strong className="text-[var(--studio-ink)]">Cloud and infrastructure</strong> — our API, database,
            and application may run on providers such as{' '}
            <ExternalPrivacyLink href="https://aws.amazon.com/privacy/">
              Amazon Web Services (Privacy Notice)
            </ExternalPrivacyLink>
            . The exact vendors depend on how Adfini is deployed; those providers process data needed
            to host and transmit our service.
          </li>
        </ul>
        <p className="text-[var(--studio-ink)]">
          URLs and policy titles may change when vendors update their sites. If a link stops working,
          search the provider&apos;s site for their current privacy policy.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Cookies and similar technologies</h2>
        <p>
          We use cookies and similar mechanisms as needed for authentication (for example, session or
          token cookies set by our API or Firebase), security, and basic site functionality.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Data retention</h2>
        <p>
          We retain information for as long as your account is active and as needed to provide the
          service, comply with legal obligations, resolve disputes, and enforce agreements. Retention
          for specific categories may vary based on backups, logs, and legal requirements.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Your choices and rights</h2>
        <p>
          Depending on where you live, you may have rights to access, correct, delete, or export
          personal data, or to object to or restrict certain processing. To exercise these rights or
          request deletion of your account and associated data, contact us at{' '}
          <a className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>
          . We may need to verify your request before proceeding.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">International transfers</h2>
        <p>
          Our service may be operated from or processed in countries other than your own. Where
          required, we use appropriate safeguards or rely on permitted transfer mechanisms.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Children</h2>
        <p>
          The service is not directed at children under 13 (or the minimum age in your
          jurisdiction). We do not knowingly collect personal information from children.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Changes</h2>
        <p>
          We may update this policy from time to time. We will post the updated version on this page
          and revise the “Last updated” date. Material changes to how we use data may also be
          reflected in updates to our{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/terms">
            Terms of Service
          </Link>{' '}
          where appropriate.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Contact</h2>
        <p>
          Questions about this policy or your data:{' '}
          <a className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>
          . For rules governing use of the Service, see our{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/terms">
            Terms of Service
          </Link>
          . You may also visit our{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/help">
            Help &amp; support
          </Link>{' '}
          page.
        </p>
      </section>
    </LegalPageLayout>
  );
}
